import {
  OnInit,
  OnDestroy,
  ViewChild,
  Directive,
  WritableSignal,
  signal,
  effect,
  untracked,
} from '@angular/core';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { LazyLoadEvent } from 'primeng/api';
import {
  ColumnDefinitionService,
  IGridColumnGroup,
  MetricsGridConfig,
  ActiveFilterService,
  FormatService,
  GridTableNgComponent,
  GridSortingService,
} from '../core';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '../models';
import { HeaderService } from '../header';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { SavedViewSerialized } from '@/app/modules/saved-views/types';
import { savedViewDeserializer } from './geography-utils';
import { createGridSerializer } from '@/app/modules/saved-views/saved-views-serializer';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Directive()
export abstract class GeographyComponent implements OnInit, OnDestroy {
  loading = true;
  data: any;
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg;
  totalCount = 0;
  frozenWidth: string;
  gridConfig: WritableSignal<MetricsGridConfig> = signal(null);
  hasAccess = false;
  hasData = false;
  dataKey: string;
  public pageName = '';
  protected subscriptions: Array<Subscription> = [];
  protected gridName = 'GEOGRAPHY_GRID';
  protected useExpandedGrid = false;
  @ViewChild('grid') grid: GridTableNgComponent;
  @ViewChild('gridExpanded') gridExpanded: GridTableNgComponent;

  constructor(
    protected authenticationService: AuthenticationService,
    protected filterService: ActiveFilterService,
    protected columnService: ColumnDefinitionService,
    protected headerService: HeaderService,
    protected formatService: FormatService,
    public translateService: TranslateService,
    protected gridSortingService: GridSortingService,
    protected router: Router,
    private savedViewsService: SavedViewsService,
    protected featureFlagService: FeatureFlagService
  ) {
    // React to saved view changes - track the view NAME to prevent infinite loops
    effect(() => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      // We don't want the effect to re-run when lastAppliedViewName changes
      const lastApplied = untracked(
        () => this.savedViewsService.previouslySelectedViewsStack()[0] ?? null
      );
      if (viewName === lastApplied) {
        return;
      }

      const hasGridConfig = untracked(() => !!this.gridConfig());

      if (selectedView && hasGridConfig) {
        // Call handler in untracked context to avoid tracking any signal reads inside
        untracked(async () => {
          const currentConfig = this.gridConfig();

          // Apply the saved view changes to the grid config
          const response = await this.savedViewsService.handleSavedViewChange(
            selectedView,
            currentConfig,
            savedViewDeserializer
          );

          if (!response || !response.config) {
            return;
          }

          // Update the signal - this will automatically trigger change detection
          this.gridConfig.set(response.config);

          // Update the gridSortingService - this is the single source of truth
          // The sorting getter will automatically read from this service
          this.gridSortingService.setSortOption(
            response.sorting.key,
            response.sorting.direction === 'desc' // setSortOption expects descending as boolean
          );

          // Update local sorting for template binding (avoids getter performance issues)
          this.sorting = this.gridSortingService.getSortOptionsNg();

          this.paging.page = 1;
          if (this.grid) {
            this.grid.resetPaginator();
          }

          this.load();

          // Signal that the full saved view application is complete
          this.savedViewsService.notifyViewApplied();
        });
      }
    });
  }

  ngOnInit() {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');

    if (areSavedViewsEnabled) {
      this.getSerializer = createGridSerializer({
        gridName: this.gridName,
        gridConfig: this.gridConfig,
        gridSortingService: this.gridSortingService,
      });
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }

    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptionsNg();

    this.gridConfig.set(this.configureGrid());
    this.beginLoad();
    this.subscriptions.push(
      this.filterService.filterChange.subscribe(() => {
        // If saved views is currently applying a view or its filters, skip —
        // the saved view effect will handle load.
        if (this.savedViewsService.isApplying()) {
          return;
        }
        this.paging.page = 1;
        if (this.grid) {
          this.grid.resetPaginator();
        }
        if (this.gridExpanded) {
          this.gridExpanded.resetPaginator();
        }
        this.load();
      })
    );

    this.subscriptions.push(
      this.authenticationService.userInfoView.subscribe((userInfo) => {
        this.hasAccess = userInfo.HasClientAccessToDownloads;
        this.setGridExportEnabled();
      })
    );

    this.translateService.onLangChange.subscribe(() => this.load());
  }

  ngOnDestroy() {
    _.each(this.subscriptions, (s) => s.unsubscribe());
  }

  abstract loadData(): Observable<any>;
  abstract exportExcel(args: any);
  abstract getFixedColumnGroup(): IGridColumnGroup;
  abstract configureGrid(): MetricsGridConfig;

  beginLoad = async () => {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      await this.savedViewsService.load();
    }

    this.load();
  };

  load = () => {
    this.loading = true;
    this.loadData().subscribe((results) => {
      this.loading = false;

      if (results.Items && results.Items.length > 0) {
        this.data = results.Items.splice(1);
        this.frozenRows = [results.Items[0]];
      } else {
        this.data = [];
        this.frozenRows = [];
      }

      this.totalCount = results.TotalCount;

      this.hasData = results && !!results.PageCount;
      this.setGridExportEnabled();
      this.authenticationService.finishSentryTransaction();
    });
  };

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }

    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  private setGridExportEnabled() {
    this.gridConfig.update((config) => {
      config.enableExcelExport = this.exportEnabled();
      return config;
    });
  }

  public getSerializer = null;

  changeLazyLoad = (event: LazyLoadEvent) => {
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    this.sorting.sortField =
      event.sortField || this.gridSortingService.defaultSortColumn;
    this.sorting.sortOrder = event.sortOrder || -1;

    this.load();
  };

  getColumnGroups = (): IGridColumnGroup[] => {
    return this.columnService.ExtendedMetricSetColumnGroups();
  };

  exportEnabled = (): boolean => {
    return this.gridConfig() && this.hasData && this.hasAccess;
  };

  protected getSorting = (): SortOptions => {
    return new SortOptions(
      this.sorting.sortField,
      !(this.sorting.sortOrder === 1)
    );
  };
}
